import { Controller, FieldValues, useFormContext } from 'react-hook-form';
import { ErrorRenderType, InputError } from 'src/components/Tooltip';
import { ReactNode, useState } from 'react';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import classNames from 'classnames';
import { InputTypes } from 'src/types';
import { sanitizeOutgoingContent } from 'src/utils';
import { RegisterOptions } from 'react-hook-form/dist/types/validator';

export interface InputProps extends FieldValues {
  name: string;
  onChangeHandler?: (data: string) => void;
  onBlurHandler?: (data: string) => void;
  className?: string;
  errorMessage?: string | ReactNode;
  isInteger?: boolean;
  type?: InputTypes;
  hasErrorBorder?: boolean;
  required?: boolean | string;
  autoComplete?: string;
  rules?: Omit<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;
  errorRenderType?: ErrorRenderType;
}

const SVG_SIZE = 20;

// TODO(olha): move error condition inside component
export const FormInput = ({
  name,
  onChangeHandler,
  onBlurHandler,
  className,
  errorMessage,
  isInteger,
  type = 'text',
  hasErrorBorder = false,
  required = false,
  autoComplete,
  rules = {},
  errorRenderType = 'tooltip',
  ...rest
}: InputProps) => {
  const {
    control,
    formState: { errors },
    setError,
  } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required,
        ...rules,
      }}
      render={({ field: { onChange, value, onBlur } }) => (
        <div
          lang="en-US"
          className={classNames('nj-input-wrapper', {
            isPassword,
            hasErrorBorder: !!errors[name] || hasErrorBorder,
          })}
        >
          <input
            value={value || ''}
            onBlur={(e) => {
              // required rule is ignored if the field contains only spaces https://github.com/react-hook-form/react-hook-form/issues/1650
              if (required && !e.target.value.trim()) {
                setError(name, {
                  message: typeof required === 'string' ? required : undefined,
                });
              }
              onBlur();
              if (onBlurHandler) {
                onBlurHandler(sanitizeOutgoingContent(e.target.value));
              }
            }}
            onChange={(e) => {
              if (isInteger) {
                if (isNaN(parseInt(e.target.value))) {
                  return;
                }
              }

              const value = Number(e.target.value);
              if (
                !!e.target.value &&
                ((typeof rest.min === 'number' && value < rest.min) ||
                  (typeof rest.max === 'number' && value > rest.max))
              ) {
                return;
              } else {
                onChange(e);
                if (onChangeHandler) {
                  onChangeHandler(sanitizeOutgoingContent(e.target.value));
                }
              }
            }}
            className={className}
            lang="en-US"
            type={isPassword ? (showPassword ? 'text' : 'password') : type}
            // attn: a hack to make autocomplete work, cancel out in form tag
            autoComplete={autoComplete ? autoComplete : 'new-password'}
            {...rest}
          />
          {isPassword && (
            <button
              className="nj-input-field-password-icon"
              onClick={() => setShowPassword(!showPassword)}
              type="button"
            >
              {showPassword ? (
                <EyeSlash size={SVG_SIZE} />
              ) : (
                <Eye size={SVG_SIZE} />
              )}
            </button>
          )}
          {(errorMessage || !!errors[name]?.message) && (
            <InputError
              renderType={errorRenderType}
              title={errorMessage || errors[name]?.message?.toString() || ''}
            />
          )}
        </div>
      )}
    />
  );
};
